import { PageController } from '@client/components/_core/layout/PageController';
import { Filters } from '@client/components/_ui/data-display/Filters';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { ContainerHeader } from '@client/components/_ui/display/ContainerHeader';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { FinancialSectionSelect } from '@client/components/claims/financials/FinancialSectionSelect';
import { TransactionNatureSelect } from '@client/components/claims/financials/TransactionNatureSelect';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useAntdTable } from '@client/hooks/table/useAntdTable';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { DEFAULT_ACTION_COLUMN } from '@client/utils';
import { TRANSACTION_NATURE_LABELS, type TransactionNature } from '@shared/constants/business/transaction-type';
import { type Falsy, isTruthy } from '@shared/utils/core/boolean';
import { toYesNo } from '@shared/utils/core/general';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Form, Select, Table } from 'antd';
import { useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/financials/$id/transaction-types',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Transaction types',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <TransactionTypeSearch />
        </PageController>
    );
}

type FiltersForm = Partial<{
    financialSectionId: bigint;
    isAutoReserveEnabled: boolean;
    transactionNature: TransactionNature;
}>;

const FormItem = Form.Item<FiltersForm>;

function TransactionTypeSearch() {
    const orgId = useOrgId();
    const { id: productTypeId } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.transactionType.deleteTransactionType.useMutation();
    const navigate = useNavigate();

    const [input, setInput] = useState<RouterInputs['transactionType']['listTransactionTypes']>({
        limit: 10,
        offset: 0,
    });

    const { data, isPending } = trpc.transactionType.listTransactionTypes.useQuery({
        ...input,
        where: {
            ...input.where,
            productTypeId,
        },
    });

    const [form] = Form.useForm<FiltersForm>();

    const { tableProps } = useAntdTable({
        rowKey: 'id',
        data: {
            rows: data?.rows,
            loading: isPending,
            total: data?.total,
        },
        onQueryVariableChange(options) {
            setInput((prev) => ({
                ...prev,
                limit: options?.limit || 10,
                offset: options?.offset || 0,
                orderBy: options?.orderBy?.length ? options?.orderBy : prev.orderBy,
            }));
        },
        columns: [
            {
                title: 'Name',
                dataIndex: 'name',
                sortBy: 'name',
            },
            {
                title: 'Section',
                dataIndex: 'FinancialSection.title',
                sortBy: 'FinancialSection.title',
            },
            {
                title: 'Type',
                dataIndex: 'transactionNature',
                sortBy: 'transactionNature',
                render(_, { transactionNature }) {
                    return TRANSACTION_NATURE_LABELS[transactionNature];
                },
            },
            {
                title: 'Use auto-reserve',
                dataIndex: 'isAutoReserveEnabled',
                sortBy: 'isAutoReserveEnabled',
                render(value) {
                    return value === null ? '-' : toYesNo(value);
                },
            },
            {
                ...DEFAULT_ACTION_COLUMN,
                render(_, { id }) {
                    return (
                        <Button.Delete
                            onConfirm={(callback) =>
                                mutate(
                                    { id },
                                    {
                                        onSuccess() {
                                            callback();
                                            void trpcUtils.transactionType.invalidate();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    );
                },
            },
        ],
    });

    return (
        <>
            <Outlet />
            <ContainerHeader
                features={[
                    {
                        feature: 'filters',
                        form,
                        onSearch(values) {
                            const filters: (
                                | Falsy
                                | RouterInputs['transactionType']['listTransactionTypes']['where']
                            )[] = [
                                values?.financialSectionId && { financialSectionId: values.financialSectionId },
                                values?.transactionNature && { transactionNature: values.transactionNature },
                                values?.isAutoReserveEnabled !== undefined && {
                                    isAutoReserveEnabled: values.isAutoReserveEnabled,
                                },
                            ];

                            setInput((prev) => ({
                                ...prev,
                                where: {
                                    AND: filters.filter(isTruthy),
                                },
                            }));
                        },
                        children: (
                            <>
                                <Filters.Item>
                                    <FormItem label="Section" name="financialSectionId">
                                        <FinancialSectionSelect productTypeId={productTypeId} />
                                    </FormItem>
                                </Filters.Item>
                                <Filters.Item>
                                    <FormItem label="Type" name="transactionNature">
                                        <TransactionNatureSelect />
                                    </FormItem>
                                </Filters.Item>
                                <Filters.Item>
                                    <FormItem label="Use auto-reserve" name="isAutoReserveEnabled">
                                        <Select
                                            options={[
                                                { label: 'Yes', value: true },
                                                { label: 'No', value: false },
                                            ]}
                                            allowClear
                                        />
                                    </FormItem>
                                </Filters.Item>
                            </>
                        ),
                    },
                ]}
                primaryActionAddon={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/claims-products/product-types/financials/$id/transaction-types/add"
                        params={{ orgId, id: productTypeId }}
                        variant="primary"
                        label="New transaction type"
                    />
                }
            />
            <Table
                size="small"
                scroll={{ x: 'max-content' }}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/product-types/financials/$id/transaction-types/edit/$transactionTypeId',
                                params: {
                                    orgId,
                                    id: productTypeId,
                                    transactionTypeId: id,
                                },
                            },
                            e,
                        );
                    },
                })}
                {...tableProps}
            />
        </>
    );
}
